import React, { useState } from 'react';
import { useLocation, Link, Routes, Route, Navigate } from 'react-router-dom';
import ApiService from '../../services/api';
import { getToken } from '../../utils/auth';
import { useNotification } from '../../context/NotificationContext';

// Senate Lobbying Component
const SenateLobbyingData = () => {
  const [symbol, setSymbol] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [lobbyingData, setLobbyingData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getSenateLobbying(symbol, fromDate, toDate);
      setLobbyingData(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching senate lobbying data:', error);
      showNotification(
        'Failed to fetch senate lobbying data. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Senate Lobbying Data</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Lobbying Data'}
            </button>
          </div>
        </div>
      </form>

      {lobbyingData.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registrant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issue
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lobbyingData.map((data, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">{data.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    ${data.amount?.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{data.client}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {data.registrant}
                  </td>
                  <td className="px-6 py-4">{data.issue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Government Spending Component
const GovernmentSpendingData = () => {
  const [symbol, setSymbol] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [country, setCountry] = useState<string>('USA');
  const [spendingData, setSpendingData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getGovernmentSpending(
        symbol,
        fromDate,
        toDate,
        country,
      );
      setSpendingData(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching government spending data:', error);
      showNotification(
        'Failed to fetch government spending data. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Government Spending Data</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="USA"
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Spending Data'}
            </button>
          </div>
        </div>
      </form>

      {spendingData.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {spendingData.map((data, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">{data.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    ${data.amount?.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{data.agency}</td>
                  <td className="px-6 py-4">{data.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// FDA Calendar Component
const FDACalendarData = () => {
  const [calendarData, setCalendarData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.getFDACalendar();
      setCalendarData(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching FDA calendar:', error);
      showNotification(
        'Failed to fetch FDA calendar. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">FDA Calendar</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Get FDA Calendar'}
          </button>
        </div>
      </form>

      {calendarData.length > 0 && (
        <div className="bg-white p-4 rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {calendarData.map((data, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {data.fromDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{data.toDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {data.eventDescription}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Insider Data Component
const InsiderData = () => {
  const [symbol, setSymbol] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [sentimentData, setSentimentData] = useState<any[]>([]);
  const [transactionsData, setTransactionsData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);

      // Fetch both sentiment and transactions data
      const [sentimentResult, transactionsResult] = await Promise.all([
        api.getInsiderSentiment(symbol, fromDate, toDate),
        api.getInsiderTransactions(symbol, fromDate, toDate),
      ]);

      setSentimentData(Array.isArray(sentimentResult) ? sentimentResult : []);
      setTransactionsData(
        Array.isArray(transactionsResult) ? transactionsResult : [],
      );
    } catch (error) {
      console.error('Error fetching insider data:', error);
      showNotification(
        'Failed to fetch insider data. Please try again.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Insider Data</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="AAPL"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Insider Data'}
            </button>
          </div>
        </div>
      </form>

      {sentimentData.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Insider Sentiment</h3>
          <div className="bg-white p-4 rounded shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MSPR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Change
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Insiders
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sentimentData.map((data, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {data.month}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {data.mspr?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {data.change?.toFixed(2)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {data.totalInsiders}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {transactionsData.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-4">Insider Transactions</h3>
          <div className="bg-white p-4 rounded shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shares
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactionsData.map((data, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">{data.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{data.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {data.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{data.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {data.shares?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      ${data.price?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      ${data.value?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Regulatory Data Component
const RegulatoryData = () => {
  const location = useLocation();

  return (
    <div>
      <div className="mb-6">
        <nav className="flex space-x-4 overflow-x-auto">
          <Link
            to="/intel/regulatory/lobbying"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/regulatory/lobbying'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Senate Lobbying
          </Link>
          <Link
            to="/intel/regulatory/spending"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/regulatory/spending'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Government Spending
          </Link>
          <Link
            to="/intel/regulatory/fda"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/regulatory/fda'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            FDA Calendar
          </Link>
          <Link
            to="/intel/regulatory/insider"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/regulatory/insider'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Insider Data
          </Link>
        </nav>
      </div>

      <Routes>
        <Route path="lobbying" element={<SenateLobbyingData />} />
        <Route path="spending" element={<GovernmentSpendingData />} />
        <Route path="fda" element={<FDACalendarData />} />
        <Route path="insider" element={<InsiderData />} />
        <Route
          path="*"
          element={<Navigate to="/intel/regulatory/lobbying" replace />}
        />
      </Routes>
    </div>
  );
};

export default RegulatoryData;
